import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { InlineDocView } from "./DocViewer";
import { ReplyBubble, ReplyToggle, FeedbackThread } from "./ConversationThread";
import { X, MessagesSquare, ChevronDown, Send, CheckCircle2, HelpCircle, History, Download } from "lucide-react";
import { downloadResource } from "../lib/download";

const NAVY = "#0A1F44";
const PANEL = "#112A57";
const EDGE = "#1E3A8A";

// ---------------- Commentary thread ----------------
function CommentItem({ c }) {
  if (c.author_role === "system") {
    return (
      <div data-testid={`ws-stamp-${c.id}`} className="flex items-center gap-2 px-1 py-0.5">
        <span className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
        <span className="text-[11px] font-semibold text-[#6B7280] text-center">
          {c.text} · {new Date(c.created_at).toLocaleString()}
        </span>
        <span className="flex-1 h-px" style={{ backgroundColor: "#E5E7EB" }} />
      </div>
    );
  }
  return (
    <div data-testid={`ws-comment-${c.id}`} className="rounded-lg px-3 py-2" style={{ backgroundColor: "#F3F4F6" }}>
      <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-line">{c.text}</p>
      <p className="text-[11px] mt-1 font-medium text-[#6B7280]">{c.name} · {new Date(c.created_at).toLocaleString()}</p>
    </div>
  );
}

function CommentForm({ docId, onPosted }) {
  const [name, setName] = useState(() => sessionStorage.getItem("wsCommentName") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!name.trim() || !text.trim()) { toast.error("Name and comment are required"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/comments`, { name: name.trim(), text: text.trim() });
      sessionStorage.setItem("wsCommentName", name.trim());
      setText("");
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post comment");
    } finally { setBusy(false); }
  };
  return (
    <div className="border-t p-3 space-y-2" style={{ borderColor: "#E5E7EB" }}>
      <input data-testid="ws-comment-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
        className="w-full rounded-lg px-3 py-2 text-sm border outline-none focus:ring-2 focus:ring-[#F97316]" style={{ borderColor: "#E5E7EB" }} />
      <div className="flex gap-2 items-start">
        <textarea data-testid="ws-comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="Add a comment… (use @Name to address someone)"
          className="flex-1 rounded-lg px-3 py-2 text-sm border outline-none resize-y focus:ring-2 focus:ring-[#F97316]" style={{ borderColor: "#E5E7EB" }} />
        <button data-testid="ws-comment-send" onClick={send} disabled={busy}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
          <Send size={14} /> Post
        </button>
      </div>
    </div>
  );
}

function threadLabel({ historyMode, previewOnly, canComment }) {
  if (historyMode) return "archived — read only";
  if (previewOnly) return "admin preview — posting disabled";
  if (canComment) return "internal review notes";
  return "view only";
}

function ThreadPanel({ docId, comments, canComment, onPosted, historyMode, previewOnly }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white">
      <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: "#E5E7EB" }}>
        <MessagesSquare size={15} style={{ color: "#F97316" }} />
        <span className="text-sm font-heading font-semibold text-[#1A1A1A]">Thread</span>
        <span className="text-xs text-[#9CA3AF]">{threadLabel({ historyMode, previewOnly, canComment })}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="ws-thread">
        {comments.length === 0 && <p className="text-sm text-[#9CA3AF] italic px-1 py-2">No comments yet.</p>}
        {comments.map((c) => <CommentItem key={c.id} c={c} />)}
      </div>
      {canComment && !historyMode && !previewOnly && <CommentForm docId={docId} onPosted={onPosted} />}
    </div>
  );
}

// ---------------- Open Items ----------------
function WsDecision({ dec, docId, canAct, reload }) {
  const responses = dec.responses || [];
  const completed = responses.some((r) => r.author_role !== "admin");
  const reply = async (text) => {
    await api.post(`/documents/${docId}/decisions/${dec.id}/responses`, { name: "", response_text: text });
    toast.success("Response sent");
    reload();
  };
  return (
    <div data-testid={`ws-decision-${dec.id}`} className="rounded-lg border p-3" style={{ backgroundColor: NAVY, borderColor: EDGE }}>
      <div className="flex items-center gap-2 mb-1.5">
        {completed
          ? <CheckCircle2 size={14} style={{ color: "#10B981" }} className="flex-shrink-0" />
          : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#F97316" }} />}
        <p className="text-sm text-white font-medium">{dec.title}</p>
      </div>
      <div className="space-y-1.5">
        {responses.map((r) => (
          <ReplyBubble key={r.id} author={r.name} isAdmin={r.author_role === "admin"} text={r.response_text} date={r.created_at} dark
            testId={`ws-decision-msg-${r.id}`} />
        ))}
      </div>
      {canAct && <div className="mt-2"><ReplyToggle dark onSend={reply} testPrefix={`ws-decision-reply-${dec.id}`} placeholder="Respond to this decision..." /></div>}
    </div>
  );
}

function WsFeedbackItem({ f, canAct, reload }) {
  const reply = async (text) => {
    await api.post(`/feedback/${f.id}/replies`, { text });
    toast.success("Reply sent");
    reload();
  };
  return (
    <div data-testid={`ws-item-${f.id}`} className="rounded-lg border p-3" style={{ backgroundColor: NAVY, borderColor: EDGE }}>
      <p className="text-xs font-semibold text-[#9CA3AF] flex items-center gap-1.5 mb-1">
        {f.is_question || f.source_role === "worker" ? <><HelpCircle size={12} /> Question</> : "Feedback"}
        <span className="font-normal">· {f.anonymous ? "Anonymous" : f.name} · {new Date(f.created_at).toLocaleString()}</span>
      </p>
      <p className="text-sm text-white leading-relaxed">{f.feedback_text}</p>
      <FeedbackThread replies={f.replies} onSend={canAct ? reply : null} dark testPrefix={`ws-item-${f.id}`} />
    </div>
  );
}

function QuestionForm({ doc, clientId, onPosted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) { toast.error("Please type your question"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("document_id", doc.id);
      fd.append("document_title", doc.title);
      fd.append("name", "Review Portal");
      fd.append("feedback_text", text.trim());
      fd.append("is_question", "true");
      await api.post(`/clients/${clientId}/feedback`, fd);
      toast.success("Question submitted to 360 Safety Systems");
      setText("");
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit question");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: EDGE }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">Ask a question about this document</p>
      <div className="flex gap-2 items-start">
        <textarea data-testid="ws-question-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="Type your question…" className="flex-1 rounded-lg px-3 py-2 text-sm outline-none resize-y"
          style={{ backgroundColor: NAVY, border: `1px solid ${EDGE}`, color: "#fff" }} />
        <button data-testid="ws-question-send" onClick={submit} disabled={busy}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
          <Send size={14} /> Ask
        </button>
      </div>
    </div>
  );
}

function OpenItemsList({ doc, clientId, role, decisions, feedback, canAct, reload, historyMode, previewOnly }) {
  const count = decisions.length + feedback.length;
  return (
    <div className="px-3 pb-3 space-y-2 overflow-y-auto" data-testid="ws-open-items">
      {role === "manager" && !historyMode && (
        <p className="text-[11px] text-[#9CA3AF] italic px-1">Read only — decisions and questions are handled in the Review portal.</p>
      )}
      {decisions.map((dec) => <WsDecision key={dec.id} dec={dec} docId={doc.id} canAct={canAct} reload={reload} />)}
      {feedback.map((f) => <WsFeedbackItem key={f.id} f={f} canAct={canAct} reload={reload} />)}
      {count === 0 && <p className="text-sm text-[#9CA3AF] italic px-1">No open items on this document.</p>}
      {!historyMode && !previewOnly && role === "review" && <QuestionForm doc={doc} clientId={clientId} onPosted={reload} />}
    </div>
  );
}

function OpenItemsPanel({ doc, clientId, role, items, reload, historyMode, previewOnly }) {
  const [open, setOpen] = useState(false);
  const canAct = !historyMode && !previewOnly && (role === "review" || role === "admin");
  const decisions = items?.decisions || [];
  const feedback = items?.feedback || [];
  return (
    <div className="border-t flex-shrink-0 flex flex-col min-h-0" style={{ backgroundColor: PANEL, borderColor: EDGE, maxHeight: open ? "55%" : undefined }}>
      <button data-testid="ws-open-items-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left flex-shrink-0">
        <span className="text-sm font-heading font-semibold text-white flex items-center gap-2">
          Open Items
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#F97316", color: "#fff" }}>{decisions.length + feedback.length}</span>
        </span>
        <ChevronDown size={16} className={`text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <OpenItemsList doc={doc} clientId={clientId} role={role} decisions={decisions} feedback={feedback}
          canAct={canAct} reload={reload} historyMode={historyMode} previewOnly={previewOnly} />
      )}
    </div>
  );
}

// ---------------- Workspace shell ----------------
function useWorkspaceData(doc, historyPackage, role) {
  const [comments, setComments] = useState([]);
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    if (historyPackage) return;
    api.get(`/documents/${doc.id}/comments`).then(({ data }) => setComments(data)).catch(() => {});
    api.get(`/documents/${doc.id}/open-items`).then(({ data }) => setItems(data)).catch(() => {});
  }, [doc.id, historyPackage]);

  useEffect(() => {
    if (historyPackage) {
      setComments(historyPackage.comments || []);
      setItems({ decisions: historyPackage.decisions || [], feedback: historyPackage.feedback || [] });
      return undefined;
    }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, historyPackage]);

  return { comments, items, load };
}

function DownloadButton({ doc }) {
  if (!doc.downloadable || !doc.main_file) return null;
  return (
    <button data-testid="ws-download" title="Download this document"
      onClick={() => downloadResource(doc.main_file.file_id, doc.main_file.filename || `${doc.title}.pdf`)}
      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-colors"
      style={{ backgroundColor: "#F97316" }}>
      <Download size={14} /> Download
    </button>
  );
}

function WorkspaceHeader({ doc, role, historyPackage, previewOnly, onClose }) {
  const HeaderIcon = historyPackage ? History : MessagesSquare;
  const pill = historyPackage
    ? `Review History · packaged ${new Date(historyPackage.created_at).toLocaleDateString()}`
    : doc.status;
  const canDownload = !historyPackage && !previewOnly && (role === "manager" || role === "review");
  return (
    <div className="h-14 flex items-center justify-between px-5 border-b flex-shrink-0" style={{ borderColor: EDGE }}>
      <div className="flex items-center gap-2.5 text-white min-w-0">
        <HeaderIcon size={18} style={{ color: "#F97316" }} />
        <span className="font-heading font-semibold truncate">{doc.title}</span>
        <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}>
          {pill}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {canDownload && <DownloadButton doc={doc} />}
        <button data-testid="ws-close" onClick={onClose} className="text-white rounded-lg p-2 transition-colors hover:bg-white/10"><X size={22} /></button>
      </div>
    </div>
  );
}

export default function ReviewWorkspace({ doc, clientId, role, onClose, historyPackage = null, previewOnly = false }) {
  const { comments, items, load } = useWorkspaceData(doc, historyPackage, role);
  const canComment = !historyPackage && (role === "manager" || role === "review");

  // Keep the file reference stable across the 5s polls so the viewer doesn't refetch/reset scroll
  const fileRef = useRef(doc.main_file);
  if (doc.main_file?.file_id !== fileRef.current?.file_id) fileRef.current = doc.main_file;

  return createPortal(
    <div data-testid="review-workspace" className="fixed inset-0 z-[60] flex flex-col" style={{ backgroundColor: NAVY }}>
      <WorkspaceHeader doc={doc} role={role} historyPackage={historyPackage} previewOnly={previewOnly} onClose={onClose} />
      <div className="flex-1 min-h-0 flex">
        <div className="w-[60%] min-w-0 border-r" style={{ borderColor: EDGE }}>
          <InlineDocView file={fileRef.current} title={doc.title} />
        </div>
        <div className="w-[40%] min-w-0 flex flex-col">
          <ThreadPanel docId={doc.id} comments={comments} canComment={canComment} onPosted={load} historyMode={!!historyPackage} previewOnly={previewOnly} />
          <OpenItemsPanel doc={doc} clientId={clientId} role={role} items={items} reload={load} historyMode={!!historyPackage} previewOnly={previewOnly} />
        </div>
      </div>
    </div>,
    document.body
  );
}

export const WORKING_STATUSES = ["In Review", "Amending", "In Consultation"];
